import "react-toastify/dist/ReactToastify.css";
import {
  setError,
  setLoading,
  setClientLoading,
  setClientError,
  setCurrentClient,
  clearCurrentClient,
  setSelectedClient,
  setAvailableClients,
  addClientToList,
  updateClientInList,
  removeClientFromList,
  setClientStats,
  setClientPermissions,
  setClientUsers,
  setClientSettings,
  setClientDashboard,
  setOnboardingStatus,
  setPagination
} from "../slices/clientSlice";
import {
  getClientList,
  getClientById,
  createClient,
  updateClient,
  deleteClient,
  getCurrentClient,
  switchClient,
  getClientStats,
  getClientPermissions,
  updateClientPermissions,
  // New API functions
  searchClients,
  getClientStatistics,
  updateClientStatus,
  updateClientPreferences,
  getClientPreferences,
  updateClientOpenings,
  getClientOpenings,
  getClientUsers as getClientUsersAPI,
  addClientUser,
  createClientAdmin,
  createClientEmployee,
  updateClientUser,
  removeClientUser,
  getClientAdmins,
  getClientEmployees,
  getClientDashboard,
  getClientPipeline,
  getClientEvaluationAnalytics
} from "../../utils/clientAPI";
import { setSelectedJD, setJDList, setFilters as setFiltersJds, setParseLoading, setGenerateLoading } from "../slices/jdsSlice";
import { loadCandidateData, setFilteredCandidates, setForwardedCandidates, setForwardingLoading, clearAllFilters } from "../slices/candidatesSlice";

// Get client list
export const getClientListAction = (params = {}) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getClientList(params);
    console.log("Client list response:", response);
    if (response.success && response.data) {
      dispatch(setAvailableClients(response.data || []));
      dispatch(setCurrentClient(response.data[0] || {}));

      if (response.data.pagination) {
        dispatch(setPagination(response.data.pagination));
      }
    } else {
      dispatch(setError(response.message || 'Failed to fetch client list'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client by ID
export const getClientByIdAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setClientLoading(true));
    const response = await getClientById(clientId);
    console.log("Client by ID response:", response);
    if (response.success && response.data) {
      dispatch(setSelectedClient(response.data));
    } else {
      dispatch(setClientError(response.message || 'Failed to fetch client'));
    }
    return response;
  } catch (error) {
    dispatch(setClientError(error.message));
    throw error;
  } finally {
    dispatch(setClientLoading(false));
  }
};

// Create client
export const createClientAction = (clientData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await createClient(clientData);
    console.log("Create client response:", response);
    if (response.success && response.data) {
      dispatch(addClientToList(response.data));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to create client'));
      throw new Error(response.message || 'Failed to create client');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Update client
export const updateClientAction = (clientId, clientData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await updateClient(clientId, clientData);
    console.log("Update client response:", response);
    if (response.success && response.data) {
      dispatch(updateClientInList({ clientId, updatedClient: response.data }));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to update client'));
      throw new Error(response.message || 'Failed to update client');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Delete client
export const deleteClientAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await deleteClient(clientId);
    console.log("Delete client response:", response);
    if (response.success) {
      dispatch(removeClientFromList(clientId));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to delete client'));
      throw new Error(response.message || 'Failed to delete client');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get current client
export const getCurrentClientAction = () => async (dispatch) => {
  try {
    dispatch(setClientLoading(true));
    const response = await getCurrentClient();
    console.log("Current client response:", response);
    if (response.success && response.data) {
      dispatch(setCurrentClient(response.data));
    } else {
      dispatch(setClientError(response.message || 'Failed to fetch current client'));
    }
    return response;
  } catch (error) {
    dispatch(setClientError(error.message));
    throw error;
  } finally {
    dispatch(setClientLoading(false));
  }
};

// Switch client
export const switchClientAction = (clientId, availableClients) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    console.log("Switch client response:", availableClients);
    const client = availableClients.find(c => c.client_id === clientId);
    if (client) {
      dispatch(setCurrentClient(client));
      dispatch(setSelectedJD(null));
      dispatch(loadCandidateData([]));
      dispatch(setJDList([]));
      dispatch(setForwardedCandidates([]));
      dispatch(setFilteredCandidates([]));
      dispatch(setForwardingLoading(false));
      dispatch(setParseLoading(false));
      dispatch(setError(null));
      dispatch(setFiltersJds({}));
      dispatch(clearAllFilters({}));
      dispatch(setGenerateLoading(false));
    }
    return client;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client statistics
export const getClientStatsAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getClientStats(clientId);
    console.log("Client stats response:", response);
    if (response.success && response.data) {
      dispatch(setClientStats(response.data));
    } else {
      dispatch(setError(response.message || 'Failed to fetch client statistics'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client permissions
export const getClientPermissionsAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getClientPermissions(clientId);
    console.log("Client permissions response:", response);
    if (response.success && response.data) {
      dispatch(setClientPermissions(response.data));
    } else {
      dispatch(setError(response.message || 'Failed to fetch client permissions'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Update client permissions
export const updateClientPermissionsAction = (clientId, permissions) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await updateClientPermissions(clientId, permissions);
    console.log("Update client permissions response:", response);
    if (response.success && response.data) {
      dispatch(setClientPermissions(response.data));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to update client permissions'));
      throw new Error(response.message || 'Failed to update client permissions');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Clear current client
export const clearCurrentClientAction = () => async (dispatch) => {
  dispatch(clearCurrentClient());
};

// Legacy actions for backward compatibility
export const fetchAllClients = getClientListAction;
export const updatePreferences = updateClientAction;

// Legacy placeholder actions for backward compatibility
export const getClientUsersLegacy = () => async (dispatch) => {
  // Placeholder for now
  return { success: true, data: [] };
};

export const addClientUserLegacy = () => async (dispatch) => {
  // Placeholder for now
  return { success: true, data: {} };
};

export const removeClientUserLegacy = () => async (dispatch) => {
  // Placeholder for now
  return { success: true };
};

// NEW ACTIONS BASED ON CLIENT MANAGEMENT DOCUMENTATION

// Search clients action
export const searchClientsAction = (query) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await searchClients(query);
    console.log("Search clients response:", response);
    if (response.success && response.data) {
      dispatch(setAvailableClients(response.data || []));
    } else {
      dispatch(setError(response.message || 'Failed to search clients'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client statistics action (admin dashboard)
export const getClientStatisticsAction = () => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getClientStatistics();
    console.log("Client statistics response:", response);
    if (response.success && response.data) {
      dispatch(setClientStats(response.data));
    } else {
      dispatch(setError(response.message || 'Failed to fetch client statistics'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Update client status action
export const updateClientStatusAction = (clientId, statusData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await updateClientStatus(clientId, statusData);
    console.log("Update client status response:", response);
    if (response.success && response.data) {
      // Update the client in the list
      dispatch(updateClientInList({ 
        clientId, 
        updatedClient: { is_active: statusData.is_active } 
      }));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to update client status'));
      throw new Error(response.message || 'Failed to update client status');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Update client preferences action
export const updateClientPreferencesAction = (clientId, preferencesData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await updateClientPreferences(clientId, preferencesData);
    console.log("Update client preferences response:", response);
    if (response.success && response.data) {
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to update client preferences'));
      throw new Error(response.message || 'Failed to update client preferences');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client preferences action
export const getClientPreferencesAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setClientLoading(true));
    const response = await getClientPreferences(clientId);
    console.log("Get client preferences response:", response);
    if (response.success && response.data) {
      dispatch(setClientSettings(response.data));
    } else {
      dispatch(setClientError(response.message || 'Failed to fetch client preferences'));
    }
    return response;
  } catch (error) {
    dispatch(setClientError(error.message));
    throw error;
  } finally {
    dispatch(setClientLoading(false));
  }
};

// Update client openings action
export const updateClientOpeningsAction = (clientId, openingsData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await updateClientOpenings(clientId, openingsData);
    console.log("Update client openings response:", response);
    if (response.success && response.data) {
      // Update the client in the list
      dispatch(updateClientInList({ 
        clientId, 
        updatedClient: { current_openings: openingsData.current_openings } 
      }));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to update client openings'));
      throw new Error(response.message || 'Failed to update client openings');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client openings action
export const getClientOpeningsAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setClientLoading(true));
    const response = await getClientOpenings(clientId);
    console.log("Get client openings response:", response);
    if (response.success && response.data) {
      return response;
    } else {
      dispatch(setClientError(response.message || 'Failed to fetch client openings'));
    }
    return response;
  } catch (error) {
    dispatch(setClientError(error.message));
    throw error;
  } finally {
    dispatch(setClientLoading(false));
  }
};

// Get client users action
export const getClientUsersAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getClientUsersAPI(clientId);
    console.log("Get client users response:", response);
    if (response.success && response.data) {
      dispatch(setClientUsers(response.data.users || []));
    } else {
      dispatch(setError(response.message || 'Failed to fetch client users'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Add client user action
export const addClientUserAction = (clientId, userData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await addClientUser(clientId, userData);
    console.log("Add client user response:", response);
    if (response.success && response.data) {
      // Refresh client users list
      dispatch(getClientUsersAction(clientId));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to add client user'));
      throw new Error(response.message || 'Failed to add client user');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Create client admin action
export const createClientAdminAction = (clientId, userData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await createClientAdmin(clientId, userData);
    console.log("Create client admin response:", response);
    if (response.success && response.data) {
      // Refresh client users list
      dispatch(getClientUsersAction(clientId));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to create client admin'));
      throw new Error(response.message || 'Failed to create client admin');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Create client employee action
export const createClientEmployeeAction = (clientId, userData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await createClientEmployee(clientId, userData);
    console.log("Create client employee response:", response);
    if (response.success && response.data) {
      // Refresh client users list
      dispatch(getClientUsersAction(clientId));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to create client employee'));
      throw new Error(response.message || 'Failed to create client employee');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Update client user action
export const updateClientUserAction = (clientId, userId, userData) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await updateClientUser(clientId, userId, userData);
    console.log("Update client user response:", response);
    if (response.success && response.data) {
      // Refresh client users list
      dispatch(getClientUsersAction(clientId));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to update client user'));
      throw new Error(response.message || 'Failed to update client user');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Remove client user action
export const removeClientUserAction = (clientId, userId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await removeClientUser(clientId, userId);
    console.log("Remove client user response:", response);
    if (response.success) {
      // Refresh client users list
      dispatch(getClientUsersAction(clientId));
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to remove client user'));
      throw new Error(response.message || 'Failed to remove client user');
    }
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client admins action
export const getClientAdminsAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getClientAdmins(clientId);
    console.log("Get client admins response:", response);
    if (response.success && response.data) {
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to fetch client admins'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client employees action
export const getClientEmployeesAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const response = await getClientEmployees(clientId);
    console.log("Get client employees response:", response);
    if (response.success && response.data) {
      return response;
    } else {
      dispatch(setError(response.message || 'Failed to fetch client employees'));
    }
    return response;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  } finally {
    dispatch(setLoading(false));
  }
};

// Get client dashboard action
export const getClientDashboardAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setClientLoading(true));
    const response = await getClientDashboard(clientId);
    console.log("Get client dashboard response:", response);
    if (response.success && response.data) {
      dispatch(setClientDashboard(response.data));
    } else {
      dispatch(setClientError(response.message || 'Failed to fetch client dashboard'));
    }
    return response;
  } catch (error) {
    dispatch(setClientError(error.message));
    throw error;
  } finally {
    dispatch(setClientLoading(false));
  }
};

// Get client pipeline action
export const getClientPipelineAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setClientLoading(true));
    const response = await getClientPipeline(clientId);
    console.log("Get client pipeline response:", response);
    if (response.success && response.data) {
      return response;
    } else {
      dispatch(setClientError(response.message || 'Failed to fetch client pipeline'));
    }
    return response;
  } catch (error) {
    dispatch(setClientError(error.message));
    throw error;
  } finally {
    dispatch(setClientLoading(false));
  }
};

// Get client evaluation analytics action
export const getClientEvaluationAnalyticsAction = (clientId) => async (dispatch) => {
  try {
    dispatch(setClientLoading(true));
    const response = await getClientEvaluationAnalytics(clientId);
    console.log("Get client evaluation analytics response:", response);
    if (response.success && response.data) {
      return response;
    } else {
      dispatch(setClientError(response.message || 'Failed to fetch client evaluation analytics'));
    }
    return response;
  } catch (error) {
    dispatch(setClientError(error.message));
    throw error;
  } finally {
    dispatch(setClientLoading(false));
  }
};
